import { defineStore } from 'pinia';
import axios from 'axios';
import type { AuthResponse, ClubRole, Role, UserProfile } from '@/types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';
const STORAGE_KEY = 'huddle.auth';

interface PersistedAuth {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: string | null;
  user: UserProfile | null;
}

function load(): PersistedAuth {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as PersistedAuth;
      if (p.user) {
        p.user = {
          ...p.user,
          memberships: (p.user.memberships ?? []).map((m) => ({
            ...m,
            // Older persisted sessions don't have `logoUrl`. Default to null so the
            // sidebar falls back to «Планёрка» branding until /auth/me refreshes it.
            logoUrl: (m as Partial<{ logoUrl: string | null }>).logoUrl ?? null,
          })),
          dateOfBirth: p.user.dateOfBirth ?? null,
          phoneNumber: p.user.phoneNumber ?? null,
          photoUrl: p.user.photoUrl ?? null,
        };
      }
      return p;
    }
  } catch {
    /* ignore */
  }
  return { accessToken: null, refreshToken: null, expiresAt: null, user: null };
}

export const useAuthStore = defineStore('auth', {
  state: () => load(),
  getters: {
    isAuthenticated: (s) => !!s.accessToken && !!s.user,
    role: (s): Role | null => (s.user?.role as Role | undefined) ?? null,
    isPlatformAdmin: (s) => s.user?.role === 'PlatformAdmin',
    memberships: (s) => s.user?.memberships ?? [],
    roleInClub: (s) => (clubId: string | null | undefined): ClubRole | null => {
      if (!clubId) return null;
      const m = s.user?.memberships?.find((x) => x.clubId === clubId);
      return (m?.roleInClub as ClubRole | undefined) ?? null;
    },
    /** True if the user is HeadCoach or Coach in at least one club. Used for cross-club UI gates. */
    isCoachAnywhere: (s) =>
      (s.user?.memberships ?? []).some(
        (m) => m.roleInClub === 'HeadCoach' || m.roleInClub === 'Coach',
      ),
  },
  actions: {
    persist() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.$state));
    },
    setAuth(resp: AuthResponse) {
      this.accessToken = resp.accessToken;
      this.refreshToken = resp.refreshToken;
      this.expiresAt = resp.expiresAtUtc;
      this.user = resp.user;
      this.persist();
    },
    async login(email: string, password: string) {
      const { data } = await axios.post<AuthResponse>(`${BASE}/auth/login`, { email, password });
      this.setAuth(data);
    },
    async registerByInvite(inviteCode: string, email: string, password: string, fullName: string) {
      const { data } = await axios.post<AuthResponse>(`${BASE}/auth/register-by-invite`, {
        inviteCode,
        email,
        password,
        fullName,
      });
      this.setAuth(data);
    },
    async redeemInvite(code: string) {
      const { api } = await import('@/api/client');
      const { data } = await api.post<{ clubId: string }>(`/invites/${code}/redeem`);
      await this.fetchMe();
      return data.clubId;
    },
    async refresh(): Promise<string | null> {
      if (!this.refreshToken) return null;
      try {
        const { data } = await axios.post<AuthResponse>(`${BASE}/auth/refresh`, {
          refreshToken: this.refreshToken,
        });
        this.setAuth(data);
        return data.accessToken;
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } }).response?.status;
        // Only kill the session when the server *explicitly* rejects the refresh
        // token (400/401/403). On transient errors (network down, backend
        // restarting, 5xx) we keep the tokens so the user can recover after the
        // backend comes back without having to log in again.
        if (status === 400 || status === 401 || status === 403) {
          this.logout();
        }
        return null;
      }
    },
    isAccessTokenFresh(skewSeconds = 30): boolean {
      if (!this.accessToken || !this.expiresAt) return false;
      const expiry = Date.parse(this.expiresAt);
      if (Number.isNaN(expiry)) return false;
      return expiry - skewSeconds * 1000 > Date.now();
    },
    async ensureFreshAccessToken(): Promise<boolean> {
      // Called on app boot to proactively refresh an expired access token before
      // any UI components fire parallel requests that would all race on 401s.
      if (!this.refreshToken) return !!this.accessToken;
      if (this.isAccessTokenFresh()) return true;
      const t = await this.refresh();
      return !!t;
    },
    async fetchMe() {
      const { api } = await import('@/api/client');
      const { data } = await api.get<UserProfile>('/auth/me');
      this.user = data;
      this.persist();
    },
    async updateProfile(payload: {
      fullName: string;
      dateOfBirth: string | null;
      phoneNumber: string | null;
    }) {
      const { api } = await import('@/api/client');
      const { data } = await api.put<UserProfile>('/profile', payload);
      this.user = data;
      this.persist();
    },
    async uploadProfilePhoto(file: File) {
      const { api } = await import('@/api/client');
      const form = new FormData();
      form.append('file', file);
      await api.post('/profile/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await this.fetchMe();
    },
    logout() {
      this.accessToken = null;
      this.refreshToken = null;
      this.expiresAt = null;
      this.user = null;
      localStorage.removeItem(STORAGE_KEY);
    },
  },
});
